import React, {
  createContext,
  useState,
  useEffect,
  type ReactNode,
} from "react";
import axios from "axios";
import { Amplify, Auth } from "aws-amplify";
import type { CognitoUser } from "amazon-cognito-identity-js";
import awsExports from "./aws-exports";
import type { CurrentUser } from "./types/user";

export type AuthState = {
  user: CognitoUser | null;
  state: "signedIn" | "signIn";
};

export type TokenContextValue = {
  user: CurrentUser | null;
  setUser: React.Dispatch<React.SetStateAction<CurrentUser | null>>;
  authState: AuthState | null;
  setAuthState: React.Dispatch<React.SetStateAction<AuthState | null>>;
  isLoading: boolean;
  fetchUserData: () => Promise<void>;
};

export const TokenContext = createContext<TokenContextValue | undefined>(
  undefined
);

type TokenProviderProps = {
  children: ReactNode;
};

export const TokenProvider = ({ children }: TokenProviderProps) => {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [authState, setAuthState] = useState<AuthState | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUserData = async () => {
    Amplify.configure(awsExports);
    Auth.currentAuthenticatedUser()
      .then((cognitoUser: CognitoUser) => {
        const accessToken = cognitoUser
          .getSignInUserSession()
          ?.getAccessToken()
          .getJwtToken();
        const apiUrl = `${process.env.REACT_APP_BASE_URL}/user/0`;
        const headers = {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        };
        axios
          .get<{ current_user: CurrentUser }>(apiUrl, headers)
          .then((response) => {
            const data = response.data.current_user;
            setUser(data);
            setAuthState({ user: cognitoUser, state: "signedIn" });
          })
          .catch((error) => {
            console.error("TokenProvider.fetchUserData Error: ", error);
          })
          .finally(() => {
            setIsLoading(false);
          });
      })
      .catch(() => {
        setAuthState({ user: null, state: "signIn" });
        setIsLoading(false);
      });
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  return (
    <TokenContext.Provider
      value={{
        user,
        setUser,
        authState,
        setAuthState,
        isLoading,
        fetchUserData,
      }}
    >
      {children}
    </TokenContext.Provider>
  );
};
